import { type FC, useEffect, useCallback, useRef } from 'react';
import { Flip, toast, ToastContainer } from 'react-toastify';
import { ReleaseInfo } from '@/generated/ReleaseInfo';
import { _noop } from '@/littledash';
import { IntercomUtils } from '@/utils/Intercom.utils';
import Button from '@/components/UI/Button/Button';
import { ReleaseChecker } from '@/utils/ReleaseChecker';
import { reloadForRelease } from '@/utils/ReleaseUpdate.utils';
import { ApplicationEvents } from '@/utils/ApplicationEvents';
import { isAuthRoute } from '@/support/auth';
import { web as webRoute } from '@/support/route';
import { DatadogUtils } from '@/utils/Datadog.utils';
import styles from './UpdateChecker.module.scss';

interface UpdateAlertProps {
  onUpdate: () => void;
  onDefer: () => void;
}

const UpdateAlert: FC<UpdateAlertProps> = ({ onUpdate, onDefer }) => {
  return (
    <div>
      <div className="fw6 white lh-title f5 mb2">A better Benchling In Vivo is ready</div>
      <div className="f6 lh-copy mb3 light-gray normal">
        This update includes key improvements and bug fixes. Simply refresh your browser to apply it.
      </div>
      <div className="flex justify-between">
        <Button onClick={onUpdate} className="f6">
          Refresh and apply
        </Button>
        <Button onClick={onDefer} plain className="f6">
          Defer for 1 hour
        </Button>
      </div>
    </div>
  );
};

const deferUntilValue = (): number => {
  const deferUntil = Number(sessionStorage.getItem('update-alert-defer-until') ?? '0');
  return Number.isFinite(deferUntil) ? deferUntil : 0;
};

/**
 * Where to reload to after a login-triggered release check. Read at reload time rather than
 * captured when the authenticated event fired, because the login flow navigates immediately
 * after the event while the check is a network round trip. Two defences make this independent
 * of that ordering: the single-use SSO short-lived token is stripped so a reload cannot
 * replay a spent handshake, and a URL still sitting on an authentication page falls back to
 * the dashboard so we never reload the user back into the login flow they just completed.
 */
const postAuthenticationReloadUrl = (): string => {
  const url = new URL(window.location.href);
  url.searchParams.delete('identifier');
  if (isAuthRoute(url.pathname)) {
    return new URL(webRoute('dashboard'), window.location.origin).href;
  }
  return url.href;
};

const toastId = 'update-toast';

// A `stacked` container positions its toasts against its own bottom edge, so the update alert
// needs its own container to be able to sit at the top of the viewport.
const containerId = 'update-alert';

export const UpdateChecker: FC = () => {
  const autoReloadStarted = useRef(false);

  const handleDefer = useCallback((updateVersion: string) => {
    toast.dismiss(toastId);
    const deferUntilUpdate = Date.now() + 1000 * 60 * 60; // Defer for 1 hour
    sessionStorage.setItem('update-alert-defer-until', `${deferUntilUpdate}`);
    IntercomUtils.trackEvent('user-deferred-update', {
      fromVersion: ReleaseInfo.releaseVersion,
      toVersion: updateVersion,
    });
    DatadogUtils.captureAction('user-deferred-update', {
      fromVersion: ReleaseInfo.releaseVersion,
      toVersion: updateVersion,
    });
  }, []);

  const handleUpdate = useCallback((updateVersion: string) => {
    toast.dismiss(toastId);
    IntercomUtils.trackEvent('user-applied-update', {
      fromVersion: ReleaseInfo.releaseVersion,
      toVersion: updateVersion,
    });
    DatadogUtils.captureAction('user-applied-update', {
      fromVersion: ReleaseInfo.releaseVersion,
      toVersion: updateVersion,
    });
    reloadForRelease().catch(_noop);
  }, []);

  const onUpdateAvailable = useCallback(
    ({ version }: { version: string }) => {
      if (deferUntilValue() <= Date.now() && !toast.isActive(toastId)) {
        IntercomUtils.trackEvent('update-alert-triggered', {
          fromVersion: ReleaseInfo.releaseVersion,
          toVersion: version,
        });
        DatadogUtils.captureAction('update-alert-triggered', {
          fromVersion: ReleaseInfo.releaseVersion,
          toVersion: version,
        });
        toast(<UpdateAlert onUpdate={() => handleUpdate(version)} onDefer={() => handleDefer(version)} />, {
          type: 'default',
          autoClose: false,
          closeOnClick: false,
          closeButton: false,
          toastId,
          containerId,
          transition: Flip,
        });
      }
    },
    [handleDefer, handleUpdate]
  );

  const applyUpdateOnAuthentication = useCallback((updateVersion: string) => {
    if (autoReloadStarted.current) {
      return;
    }
    autoReloadStarted.current = true;
    toast.dismiss(toastId);
    IntercomUtils.trackEvent('update-auto-applied', {
      fromVersion: ReleaseInfo.releaseVersion,
      toVersion: updateVersion,
      trigger: 'authenticated',
    });
    DatadogUtils.captureAction('update-auto-applied', {
      fromVersion: ReleaseInfo.releaseVersion,
      toVersion: updateVersion,
      trigger: 'authenticated',
    });
    reloadForRelease(postAuthenticationReloadUrl()).catch(_noop);
  }, []);

  useEffect(() => {
    const abortController = new AbortController();
    const releaseChecker = new ReleaseChecker({
      onUpdateAvailable,
    }).initialise();
    // Logging in is the one moment we know the user is starting fresh, so it is checked
    // regardless of any defer window: a deferred toast must not let someone spend a whole
    // session on a build whose chunks have already been replaced.
    ApplicationEvents.onAuthenticated(() => {
      releaseChecker
        .checkNow()
        .then((result) => {
          if (result.type === 'update-available' && !abortController.signal.aborted) {
            applyUpdateOnAuthentication(result.version);
          }
        })
        .catch(_noop);
    }, abortController.signal);
    return () => {
      abortController.abort();
      releaseChecker.destroy();
    };
  }, [applyUpdateOnAuthentication, onUpdateAvailable]);

  return (
    <ToastContainer
      containerId={containerId}
      position="top-right"
      theme="dark"
      closeOnClick={false}
      icon={false}
      className={styles.UpdateAlertContainer}
    />
  );
};
